import type { BeregnResultat } from '@/lib/calculator';
import { QUESTIONS, podcastUrl, type SvarMulighed } from '@/constants/questions';

interface ResultCardProps {
  budget: number;
  svar: Record<string, SvarMulighed>;
  resultat: BeregnResultat;
}

function formatKr(amount: number): string {
  return new Intl.NumberFormat('da-DK', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(Math.abs(amount))) + ' kr.';
}

function formatKrSigned(amount: number): string {
  return (amount < 0 ? '−' : '') + formatKr(amount);
}

function formatPct(ratio: number): string {
  return new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(ratio) * 100) + ' %';
}

export function ResultCard({ budget, svar, resultat }: ResultCardProps) {
  const { lav, høj, breakdown } = resultat;

  // Podcast-anbefalinger: sortér efter uudnyttet potentiale.
  // 'ingenting' prioriteres over 'noget'. Inden for samme niveau sorteres efter
  // hvad brugeren maksimalt kunne spare (enStorDel høj × andel × budget).
  // Spørgsmål med 'enStorDel' vises ikke — de gør det allerede godt.
  const SVAR_PRIORITET: Record<SvarMulighed, number> = { ingenting: 0, noget: 1, enStorDel: 99 };

  const podcastAnbefalinger = QUESTIONS.filter((q) => {
    const valg = svar[q.key] ?? 'ingenting';
    return valg !== 'enStorDel';
  })
    .map((q) => {
      const valg = svar[q.key] ?? 'ingenting';
      const maxPotentiale = q.andel * q.pct['enStorDel'][1] * budget;
      return { q, valg, maxPotentiale };
    })
    .sort((a, b) => {
      const prioritetForskel = SVAR_PRIORITET[a.valg] - SVAR_PRIORITET[b.valg];
      if (prioritetForskel !== 0) return prioritetForskel;
      return b.maxPotentiale - a.maxPotentiale; // højest potentiale først
    })
    .map(({ q }) => q);

  // Flatten til individuelle episoder, deduplikér på ep-nummer, tag de første 3
  const seenEps = new Set<string>();
  const anbefaledEpisoder = podcastAnbefalinger
    .flatMap((q) =>
      q.podcastEp.map((ep, i) => ({ ep, title: q.podcastTitles[i] ?? ep }))
    )
    .filter(({ ep }) => {
      if (seenEps.has(ep)) return false;
      seenEps.add(ep);
      return true;
    })
    .slice(0, 3);

  const harEnergiAdvarsel = (svar['energy'] ?? 'ingenting') === 'ingenting';

  return (
    <div className="space-y-8">
      {/* Hoved-resultat */}
      <div className="text-center space-y-2">
        <p className="text-sm font-medium text-brand uppercase tracking-widest">
          Dit optimeringspotentiale
        </p>
        <p className="text-5xl md:text-6xl font-serif font-bold text-gray-900" aria-live="polite">
          {formatKrSigned(lav)} – {formatKrSigned(høj)}
        </p>
        <p className="text-lg text-gray-500">
          Svarende til {formatPct(lav / budget)} – {formatPct(høj / budget)} af dit budget
        </p>
        <p className="text-sm text-gray-400">
          Samlet byggebudget: {formatKr(budget)}
        </p>
      </div>

      {/* Energi-advarsel */}
      {harEnergiAdvarsel && (
        <div role="alert" className="flex gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
          <span className="text-red-500 text-xl" aria-hidden="true">⚠️</span>
          <div className="text-sm text-red-800">
            <p className="font-semibold mb-0.5">Energioptimering: meromkostning på vej</p>
            <p>
              Uden aktiv energioptimering betaler I mere i drift over husets levetid.
              Vores beregning medregner −{formatKr(72_000)} i mistet besparelse.
            </p>
          </div>
        </div>
      )}

      {/* Breakdown */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Fordeling pr. område</h3>
        <div className="space-y-2">
          {breakdown.map((b) => (
            <div
              key={b.key}
              className={`flex justify-between items-center px-4 py-3 rounded-lg text-sm
                ${b.erNegativ ? 'bg-red-50 text-red-800' : 'bg-gray-50 text-gray-700'}`}
            >
              <span>{b.title ?? b.key}</span>
              <span className={`font-medium tabular-nums ${b.erNegativ ? 'text-red-600' : 'text-gray-900'}`}>
                {b.erNegativ ? '−' : ''}{formatKr(b.lav)}
                {b.lav !== b.høj && ` – ${formatKr(b.høj)}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Podcast-anbefalinger */}
      {anbefaledEpisoder.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">Anbefalede afsnit til dig</h3>
          <p className="text-sm text-gray-500 mb-3">
            De tre områder hvor du har størst potentiale — lyt og lær inden du går i gang.
          </p>
          <div className="space-y-2">
            {anbefaledEpisoder.map(({ ep, title }) => (
              <a
                key={ep}
                href={podcastUrl(ep)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-brand/5 border border-brand/10
                  hover:bg-brand/10 hover:border-brand/20 transition-colors group
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                <span className="text-xs font-mono text-brand font-semibold whitespace-nowrap">{ep}</span>
                <span className="text-sm text-gray-700 group-hover:text-gray-900 flex-1">{title}</span>
                <span className="text-gray-300 group-hover:text-brand transition-colors text-xs" aria-hidden="true">↗</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="rounded-2xl bg-gray-900 text-white p-6 space-y-4 text-center">
        <h3 className="text-xl font-serif">
          Lær præcis hvordan du realiserer besparelsen
        </h3>
        <p className="text-gray-300 text-sm">
          7 guides skrevet til dig som bygger for første gang.
          Konkret, dansk og uden unødigt fagsprog.
        </p>
        <a
          href="https://www.droemmevillaen.dk/lp/7guides"
          className="inline-block w-full py-4 px-6 rounded-xl bg-brand text-white font-semibold
            hover:bg-brand-400 transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
        >
          Køb 7 guides — 299 kr.
        </a>
        <p className="text-xs text-gray-500">
          Øjeblikkelig adgang · Ingen abonnement · 30 dages returret
        </p>
      </div>
    </div>
  );
}
